import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, apiDownload } from '../api/client';
import { usePlans, useRouters } from '../api/queries';
import { useCurrentRouter } from '../lib/current-router';
import type { Paginated, VoucherBatchDto } from '../api/types';
import { useAuth } from '../auth/auth-context';
import { VoucherBatchStatusBadge } from '../components/status';
import { Card, ConfirmDialog, Empty, ErrorAlert, Field, Modal, Pagination, Spinner } from '../components/ui';
import { formatDateTime, formatDuration, formatNumber, formatRelative, fromDateTimeInput } from '../lib/format';

export function VouchersPage() {
  const { canOnRouter } = useAuth();
  const routers = useRouters();
  const queryClient = useQueryClient();
  const { routerId } = useCurrentRouter();

  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [openBatchId, setOpenBatchId] = useState<string | null>(null);
  const [deletingBatch, setDeletingBatch] = useState<VoucherBatchDto | null>(null);
  const [pageError, setPageError] = useState<unknown>(null);

  // Otro router, otra lista: la página actual deja de tener sentido.
  useEffect(() => setPage(1), [routerId]);

  const batches = useQuery({
    queryKey: ['voucher-batches', routerId, page],
    enabled: Boolean(routerId),
    queryFn: () =>
      api.get<Paginated<VoucherBatchDto>>('/vouchers/batches', { routerId, page, pageSize: 20 }),
    // Un lote recién creado se sigue generando en el router: se refresca para ver avanzar
    // el contador sin que el operador tenga que recargar. Sin lotes pendientes se sigue
    // refrescando cada minuto para que el aviso de vencidos no se quede congelado.
    refetchInterval: (query) =>
      query.state.data?.data.some((batch) => batch.status === 'PENDING') ? 3000 : 60_000,
  });

  const totalExpired = (batches.data?.data ?? []).reduce(
    (sum, batch) => sum + (batch.expiredCount ?? 0),
    0,
  );
  const batchesWithExpired = (batches.data?.data ?? []).filter(
    (batch) => (batch.expiredCount ?? 0) > 0,
  ).length;

  const canCreateSomewhere = (routers.data ?? []).some((router) => canOnRouter(router.id, 'create'));

  const removeBatch = useMutation({
    mutationFn: (batch: VoucherBatchDto) =>
      api.del<{ deleted: number; failed: number }>(`/vouchers/batches/${batch.id}`),
    onSuccess: () => {
      setDeletingBatch(null);
      void queryClient.invalidateQueries({ queryKey: ['voucher-batches'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error) => {
      setDeletingBatch(null);
      setPageError(error);
    },
  });

  return (
    <main className="page">
      <div className="page-header">
        <div>
          <h1>Vouchers</h1>
          <p>
            Generación masiva de credenciales. Los códigos están listos al instante; las altas en el
            router siguen en segundo plano.
          </p>
        </div>
        {canCreateSomewhere && (
          <button type="button" className="primary" onClick={() => setCreating(true)}>
            Generar lote
          </button>
        )}
      </div>

      <ErrorAlert error={pageError} onDismiss={() => setPageError(null)} />

      {totalExpired > 0 && (
        <div className="alert warn">
          {formatNumber(totalExpired)} voucher{totalExpired === 1 ? '' : 's'} vencido
          {totalExpired === 1 ? '' : 's'} o caducado{totalExpired === 1 ? '' : 's'} en{' '}
          {batchesWithExpired} lote{batchesWithExpired === 1 ? '' : 's'}
          {batches.data && batches.data.meta.totalPages > 1 ? ' (de esta página)' : ''}. Ya no
          permiten iniciar sesión.
        </div>
      )}

      <Card
        footer={
          batches.data && batches.data.meta.total > 0 ? (
            <Pagination
              page={batches.data.meta.page}
              totalPages={batches.data.meta.totalPages}
              total={batches.data.meta.total}
              onPage={setPage}
            />
          ) : undefined
        }
      >
        {batches.isLoading ? (
          <div className="card-body">
            <Spinner />
          </div>
        ) : batches.data && batches.data.data.length > 0 ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Lote</th>
                  <th>Router</th>
                  <th>Plan</th>
                  <th className="num">Cantidad</th>
                  <th>Progreso</th>
                  <th>Caducan</th>
                  <th>Creado</th>
                  <th className="actions" />
                </tr>
              </thead>
              <tbody>
                {batches.data.data.map((batch) => (
                  <tr key={batch.id}>
                    <td>
                      <strong className="mono">
                        {batch.prefix || batch.suffix
                          ? `${batch.prefix}…${batch.suffix ?? ''}`
                          : '(sin prefijo)'}
                      </strong>
                      {batch.note && (
                        <div className="faint" style={{ fontSize: 12 }}>
                          {batch.note}
                        </div>
                      )}
                    </td>
                    <td>{batch.routerName}</td>
                    <td>{batch.planName}</td>
                    <td className="num">{formatNumber(batch.quantity)}</td>
                    <td>
                      <VoucherBatchStatusBadge status={batch.status} />
                      {batch.status !== 'COMPLETED' && (
                        <div className="faint" style={{ fontSize: 12 }}>
                          {formatNumber(batch.createdCount)} en router ·{' '}
                          {formatNumber(batch.failedCount)} pendientes
                        </div>
                      )}
                    </td>
                    <td>
                      {batch.expiresAt ? formatRelative(batch.expiresAt) : <span className="faint">—</span>}
                      {(batch.expiredCount ?? 0) > 0 && (
                        <div className="badge bad" style={{ marginTop: 4 }}>
                          {formatNumber(batch.expiredCount ?? 0)} vencido
                          {(batch.expiredCount ?? 0) === 1 ? '' : 's'}
                        </div>
                      )}
                    </td>
                    <td className="faint">
                      {formatRelative(batch.createdAt)}
                      {batch.createdByName && (
                        <div style={{ fontSize: 12 }}>por {batch.createdByName}</div>
                      )}
                    </td>
                    <td className="actions">
                      <button type="button" className="small" onClick={() => setOpenBatchId(batch.id)}>
                        Ver códigos
                      </button>
                      <button
                        type="button"
                        className="small"
                        style={{ marginLeft: 6 }}
                        onClick={() =>
                          void apiDownload(
                            `/vouchers/batches/${batch.id}/export.csv`,
                            `vouchers-${batch.id}.csv`,
                          ).catch(setPageError)
                        }
                      >
                        CSV
                      </button>
                      {canOnRouter(batch.routerId, 'delete') && (
                        <button
                          type="button"
                          className="small danger"
                          style={{ marginLeft: 6 }}
                          onClick={() => setDeletingBatch(batch)}
                        >
                          Eliminar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty>Todavía no has generado ningún lote de vouchers.</Empty>
        )}
      </Card>

      {creating && (
        <CreateBatchModal
          onClose={() => setCreating(false)}
          onCreated={(batch) => {
            setCreating(false);
            setOpenBatchId(batch.id);
            void queryClient.invalidateQueries({ queryKey: ['voucher-batches'] });
          }}
        />
      )}

      {openBatchId && <BatchDetailModal batchId={openBatchId} onClose={() => setOpenBatchId(null)} />}

      {deletingBatch && (
        <ConfirmDialog
          title={`Eliminar lote ${deletingBatch.prefix || '(sin prefijo)'}`}
          danger
          busy={removeBatch.isPending}
          confirmLabel={`Eliminar ${deletingBatch.quantity} voucher(s)`}
          message={
            <p>
              Se borran los {deletingBatch.quantity} códigos del MikroTik y dejan de funcionar,
              cortando las sesiones que tengan abiertas. El historial se conserva (incluida la
              deuda de facturación de quien los generó); solo desaparecen del router.
            </p>
          }
          onCancel={() => setDeletingBatch(null)}
          onConfirm={() => removeBatch.mutate(deletingBatch)}
        />
      )}
    </main>
  );
}

function CreateBatchModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (batch: VoucherBatchDto) => void;
}) {
  const { canOnRouter } = useAuth();
  const routers = useRouters();
  const current = useCurrentRouter();
  const available = (routers.data ?? []).filter(
    (router) => router.isActive && canOnRouter(router.id, 'create'),
  );

  // Se abre sobre el router de trabajo para no generar por descuido en otro.
  const [routerId, setRouterId] = useState(() =>
    available.some((router) => router.id === current.routerId) ? current.routerId : '',
  );
  const [planId, setPlanId] = useState('');
  const [quantity, setQuantity] = useState('50');
  const [prefix, setPrefix] = useState('WIFI-');
  const [suffix, setSuffix] = useState('');
  const [usernameLength, setUsernameLength] = useState('6');
  const [passwordLength, setPasswordLength] = useState('6');
  const [sameAsUsername, setSameAsUsername] = useState(false);
  const [charset, setCharset] = useState('ALNUM_NO_AMBIGUOUS');
  const [passwordCharset, setPasswordCharset] = useState('ALNUM_NO_AMBIGUOUS');
  const [expiresAt, setExpiresAt] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<unknown>(null);

  const plans = usePlans(routerId || undefined, false, 'HOTSPOT');

  const create = useMutation({
    mutationFn: () =>
      api.post<VoucherBatchDto>('/vouchers/batches', {
        routerId,
        planId,
        quantity: Number(quantity),
        prefix,
        suffix: suffix || undefined,
        usernameLength: Number(usernameLength),
        passwordLength: Number(passwordLength),
        sameAsUsername,
        charset,
        passwordCharset,
        expiresAt: fromDateTimeInput(expiresAt),
        note: note || undefined,
      }),
    onSuccess: onCreated,
    onError: (caught) => setError(caught),
  });

  return (
    <Modal
      title="Generar lote de vouchers"
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="primary"
            disabled={create.isPending || !routerId || !planId}
            onClick={() => create.mutate()}
          >
            {create.isPending ? <Spinner label="Generando…" /> : `Generar ${quantity} vouchers`}
          </button>
        </>
      }
    >
      <ErrorAlert error={error} onDismiss={() => setError(null)} />

      {available.length === 0 ? (
        <p className="muted">No tienes permiso de creación en ningún router activo.</p>
      ) : (
        <>
          <div className="form-row">
            <Field label="Router">
              <select
                value={routerId}
                onChange={(event) => {
                  setRouterId(event.target.value);
                  setPlanId('');
                }}
              >
                <option value="">Selecciona…</option>
                {available.map((router) => (
                  <option key={router.id} value={router.id}>
                    {router.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Plan">
              <select value={planId} onChange={(event) => setPlanId(event.target.value)} disabled={!routerId}>
                <option value="">Selecciona…</option>
                {(plans.data ?? []).map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Cantidad" hint="Máximo 1000 por lote">
              <input
                type="number"
                min={1}
                max={1000}
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
              />
            </Field>
          </div>

          <fieldset>
            <legend>Formato de los códigos</legend>
            <div className="form-row">
              <Field label="Prefijo">
                <input value={prefix} onChange={(event) => setPrefix(event.target.value)} placeholder="WIFI-" />
              </Field>
              <Field label="Sufijo" hint="Opcional, p. ej. @nauta.com.cu">
                <input value={suffix} onChange={(event) => setSuffix(event.target.value)} placeholder="@nauta.com.cu" />
              </Field>
              <Field label="Longitud del usuario">
                <input
                  type="number"
                  min={4}
                  max={24}
                  value={usernameLength}
                  onChange={(event) => setUsernameLength(event.target.value)}
                />
              </Field>
              <Field label="Longitud de la contraseña">
                <input
                  type="number"
                  min={4}
                  max={24}
                  value={passwordLength}
                  disabled={sameAsUsername}
                  onChange={(event) => setPasswordLength(event.target.value)}
                />
              </Field>
            </div>
            <div className="form-row">
              <Field
                label="Alfabeto del usuario"
                hint="«Sin ambiguos» excluye los que se confunden al teclear (0/O, 1/I/l, 5/S…)."
              >
                <CharsetSelect value={charset} onChange={setCharset} />
              </Field>
              <Field label="Alfabeto de la contraseña">
                <CharsetSelect
                  value={sameAsUsername ? charset : passwordCharset}
                  onChange={setPasswordCharset}
                  disabled={sameAsUsername}
                />
              </Field>
            </div>

            <p className="faint" style={{ fontSize: 12, marginTop: 0 }}>
              Ejemplo: usuario{' '}
              <span className="mono">
                {prefix}
                {sampleCode(charset, Number(usernameLength))}
                {suffix}
              </span>
              {' · '}contraseña{' '}
              <span className="mono">
                {sameAsUsername
                  ? `${prefix}${sampleCode(charset, Number(usernameLength))}${suffix}`
                  : sampleCode(passwordCharset, Number(passwordLength))}
              </span>
            </p>

            <label className="inline">
              <input
                type="checkbox"
                checked={sameAsUsername}
                onChange={(event) => setSameAsUsername(event.target.checked)}
              />
              Un solo código que sirva de usuario y contraseña
            </label>
            <p className="faint" style={{ fontSize: 12, marginBottom: 0 }}>
              Más cómodo en el portal, a cambio de la mitad de entropía.
            </p>
          </fieldset>

          <div className="form-row">
            <Field label="Caducidad" hint="Opcional, aplica a todo el lote">
              <input
                type="datetime-local"
                value={expiresAt}
                onChange={(event) => setExpiresAt(event.target.value)}
              />
            </Field>
            <Field label="Nota">
              <input
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Lote tienda centro"
              />
            </Field>
          </div>
        </>
      )}
    </Modal>
  );
}

function BatchDetailModal({ batchId, onClose }: { batchId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<unknown>(null);

  const batch = useQuery({
    queryKey: ['voucher-batch', batchId],
    queryFn: () => api.get<VoucherBatchDto>(`/vouchers/batches/${batchId}`),
    refetchInterval: (query) => (query.state.data?.status === 'PENDING' ? 2000 : false),
  });

  const retry = useMutation({
    mutationFn: () => api.post<VoucherBatchDto>(`/vouchers/batches/${batchId}/retry-sync`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['voucher-batch', batchId] });
      void queryClient.invalidateQueries({ queryKey: ['voucher-batches'] });
    },
    onError: (caught) => setError(caught),
  });

  const data = batch.data;

  return (
    <Modal
      wide
      title={data ? `Lote ${data.prefix || ''} · ${data.planName}` : 'Lote de vouchers'}
      onClose={onClose}
      footer={
        <>
          {data && data.failedCount > 0 && (
            <button type="button" disabled={retry.isPending} onClick={() => retry.mutate()}>
              {retry.isPending ? <Spinner label="Reintentando…" /> : `Reintentar ${data.failedCount} pendientes`}
            </button>
          )}
          <button
            type="button"
            onClick={() =>
              void apiDownload(`/vouchers/batches/${batchId}/export.csv`, `vouchers-${batchId}.csv`).catch(
                setError,
              )
            }
          >
            Descargar CSV
          </button>
          <button type="button" onClick={() => window.print()}>
            Imprimir
          </button>
          <button type="button" className="primary" onClick={onClose}>
            Cerrar
          </button>
        </>
      }
    >
      <ErrorAlert error={error} onDismiss={() => setError(null)} />

      {batch.isLoading || !data ? (
        <Spinner />
      ) : (
        <>
          <div className="toolbar">
            <VoucherBatchStatusBadge status={data.status} />
            <span className="muted">
              {formatNumber(data.createdCount)} de {formatNumber(data.quantity)} creados en{' '}
              {data.routerName}
            </span>
            {data.expiresAt && <span className="muted">· caducan {formatDateTime(data.expiresAt)}</span>}
            {(data.exhaustedCount ?? 0) > 0 && (
              <span className="muted">· {formatNumber(data.exhaustedCount ?? 0)} con tiempo agotado</span>
            )}
            {(data.expiredCount ?? 0) > 0 && (
              <span className="badge bad">
                {formatNumber(data.expiredCount ?? 0)} vencido
                {(data.expiredCount ?? 0) === 1 ? '' : 's'} o caducado
                {(data.expiredCount ?? 0) === 1 ? '' : 's'}
              </span>
            )}
          </div>

          {data.status === 'PENDING' && (
            <div className="alert warn">
              Las altas se están aplicando en el router. Los códigos ya son definitivos: puedes
              imprimirlos mientras termina.
            </div>
          )}

          <div className="voucher-grid">
            {(data.vouchers ?? []).map((voucher) => (
              <div className="voucher" key={voucher.id}>
                <div className="label">Usuario</div>
                <div className="code">{voucher.username}</div>
                <div className="label" style={{ marginTop: 6 }}>
                  Contraseña
                </div>
                <div className="code">{voucher.password}</div>
                {voucher.status === 'PENDING_SYNC' && (
                  <div className="badge bad" style={{ marginTop: 6 }}>
                    pendiente
                  </div>
                )}
                {voucher.timeConsumed && (
                  <div className="badge bad" style={{ marginTop: 6 }}>
                    tiempo agotado · {formatDuration(voucher.uptimeSeconds)} usados
                  </div>
                )}
                {!voucher.timeConsumed && voucher.expired && (
                  <div className="badge bad" style={{ marginTop: 6 }}>
                    vencido por fecha
                  </div>
                )}
                {!voucher.timeConsumed && voucher.uptimeSeconds > 0 && !voucher.expired && (
                  <div className="faint" style={{ marginTop: 6, fontSize: 12 }}>
                    {formatDuration(voucher.uptimeSeconds)} usados
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </Modal>
  );
}

/** Mismos alfabetos que `CHARSETS` del API; aquí sólo hacen falta para el ejemplo. */
const CHARSET_OPTIONS: { value: string; label: string; chars: string }[] = [
  { value: 'ALNUM_NO_AMBIGUOUS', label: 'Mayúsculas y dígitos sin ambiguos', chars: 'ACDEFGHJKLMNPQRTUVWXY34679' },
  { value: 'UPPER_ALNUM', label: 'Mayúsculas y dígitos', chars: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789' },
  { value: 'LOWER_ALNUM_NO_AMBIGUOUS', label: 'Minúsculas y dígitos sin ambiguos', chars: 'acdefhjkmnpqrtuvwxy34679' },
  { value: 'LOWER_ALNUM', label: 'Minúsculas y dígitos', chars: 'abcdefghijklmnopqrstuvwxyz0123456789' },
  { value: 'ALNUM', label: 'Alfanumérico completo', chars: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789' },
  { value: 'NUMERIC', label: 'Sólo dígitos', chars: '0123456789' },
];

function CharsetSelect({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <select value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
      {CHARSET_OPTIONS.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

/** Código de muestra estable (no aleatorio) para que el ejemplo no cambie al teclear. */
function sampleCode(charset: string, length: number): string {
  const chars = CHARSET_OPTIONS.find((option) => option.value === charset)?.chars ?? 'X';
  const size = Math.min(Math.max(Number.isFinite(length) ? length : 0, 0), 24);
  let out = '';
  for (let i = 0; i < size; i += 1) out += chars[(i * 7 + 3) % chars.length];
  return out;
}
